import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { getClient } from "../../services/clientService";
import { useParams } from "react-router-dom";
import { ReferenceAuthorization } from "../../interfaces/referenceAuthorization";
import { ReferenceAuthorizationInstruction } from "../../interfaces/referenceAuthorizationInstruction";
import {
  getReferenceAuthorizationBase,
  getReferenceAuthorizationInstructions,
} from "../../services/referenceAuthorizationService";
import { getAdditionalForm } from "../../services/additionalFormService";
import { HttpService } from "../../services/httpService";
import EditBaseText from "../../components/questionnaires/EditBaseText";
import AuthorizationForm, {
  Authorization,
} from "../../components/form-components/AuthorizationForm";

const EditReferenceAuthorization = () => {
  const { clientID } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [referenceAuthorization, setReferenceAuthorization] =
    useState<ReferenceAuthorization>();
  const [instructions, setInstructions] =
    useState<ReferenceAuthorizationInstruction>();
  const [pageDecorationUrl, setPageDecorationUrl] = useState<string | null>(
    null,
  );
  const [showEditBaseText, setShowEditBaseText] = useState(false);
  const [editingBaseText, setEditingBaseText] = useState<{
    key: string;
    text: string;
  } | null>(null);
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });

  useEffect(() => {
    const loadClient = async () => {
      try {
        if (clientID) {
          const numericClientID = parseInt(clientID, 10);
          const client = await getClient(numericClientID);
          setClient(client);

          const refAuthResponse = await getReferenceAuthorizationBase(
            client.clientID,
          );
          setReferenceAuthorization(refAuthResponse);

          const refAuthInstructionsResponse =
            await getReferenceAuthorizationInstructions(client.clientID, 0);
          setInstructions(refAuthInstructionsResponse);

          const pageDecorationForm = await getAdditionalForm(
            client.clientID,
            5,
          );

          if (pageDecorationForm) {
            const pageDecorationUrl = await HttpService.getFileAdmin(
              "page-decorations",
              client.clientID.toString(),
              pageDecorationForm.completedFormUrl,
            );
            setPageDecorationUrl(pageDecorationUrl);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        setErrorMessage("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [clientID]);

  const handleAuthorizationChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <>
      <div>
        <div className="content-container">
          <div className="header-container">
            <h1>Reference Authorization</h1>
            <div className="page-items-container">
              <div className="field-long">
                <label className="field-label">Client Name:</label>
                <div>
                  <input
                    className="form-control"
                    type="text"
                    value={client?.clientName || ""}
                    disabled
                    readOnly
                  />
                </div>
              </div>
              <div className="field-long">
                <label className="field-label">Abbreviation:</label>
                <div>
                  <input
                    className="form-control"
                    type="text"
                    value={client?.abbreviation}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-divider">
              <div className="loading-bar"></div>
            </div>
          ) : (
            <>
              <div className="divider"></div>
              <ul className="error">
                <li>
                  Do not edit the {"{years}"} in the instructions. The years
                  will dynamically update for that specific order when a
                  candidate is filling out the form.
                </li>{" "}
                <li>
                  Instructions (A, B and C) are used on the candidate
                  identification form as well
                </li>
              </ul>
              <div className="mt-3 data-container">
                <div
                  className="questionnaire-header page-deco-container"
                  style={{ width: "100%" }}
                >
                  {pageDecorationUrl ? (
                    <div className="page-decoration">
                      <img
                        src={pageDecorationUrl}
                        alt="Logo"
                        style={{
                          maxWidth: "100px",
                          maxHeight: "100px",
                          height: "auto",
                        }}
                      />
                    </div>
                  ) : (
                    <div className="page-decoration">
                      <div className="text">
                        <b>
                          Upload a logo on the view client page in additional
                          forms.
                        </b>
                      </div>
                    </div>
                  )}
                  <div className="text-form-header">
                    REFERENCE AUTHORIZATION
                  </div>
                  {client?.diocese2?.trim() && (
                    <div style={{ fontWeight: "bold", textAlign: "center" }}>
                      {client.diocese2.toUpperCase()}
                    </div>
                  )}
                  {client?.diocese && (
                    <div style={{ fontWeight: "bold", textAlign: "center" }}>
                      {client.diocese.toUpperCase()}
                    </div>
                  )}
                </div>

                {referenceAuthorization && (
                  <>
                    {(
                      ["baseTextOne", "baseTextTwo", "baseTextThree"] as Array<
                        keyof ReferenceAuthorization
                      >
                    ).map((key) => {
                      const currentText =
                        (referenceAuthorization[key] as string) ?? "";
                      const isEmpty = currentText.trim() === "";

                      return (
                        <div
                          key={key}
                          className={`editable-text-container hover-enabled`}
                          onMouseEnter={() => setIsHovered(key)}
                          onMouseLeave={() => setIsHovered(null)}
                        >
                          <div
                            className={`editable-text ${
                              isHovered === key ? "blurred" : ""
                            }`}
                          >
                            {isEmpty ? (
                              <span className="empty-placeholder">
                                [Click to add text]
                              </span>
                            ) : (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: currentText,
                                }}
                              />
                            )}
                          </div>

                          {isHovered === key && (
                            <button
                              className="edit-icon"
                              onClick={() => {
                                setEditingBaseText({
                                  key,
                                  text: currentText,
                                });
                                setShowEditBaseText(true);
                              }}
                            >
                              {isEmpty ? (
                                <i className="bi bi-plus"></i>
                              ) : (
                                <i className="bi bi-pencil"></i>
                              )}
                            </button>
                          )}

                          {showEditBaseText &&
                            editingBaseText?.key === key &&
                            client && (
                              <EditBaseText
                                type={"referenceAuthorization"}
                                clientID={client.clientID}
                                baseTextKey={editingBaseText.key}
                                baseText={editingBaseText.text}
                                onClose={(updatedText, successMessage) => {
                                  setReferenceAuthorization((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          [editingBaseText.key]: updatedText,
                                        }
                                      : prev,
                                  );
                                  if (successMessage) {
                                    setSuccessMessage(successMessage);
                                  }
                                  setShowEditBaseText(false);
                                }}
                              />
                            )}
                          <br />
                        </div>
                      );
                    })}
                  </>
                )}

                {instructions && (
                  <>
                    <ol className="alphabet-list">
                      {(
                        [
                          "instructionOne",
                          "instructionTwo",
                          "instructionThree",
                        ] as Array<keyof ReferenceAuthorizationInstruction>
                      ).map((key) => {
                        const currentText = (instructions[key] as string) ?? "";
                        const isEmpty = currentText.trim() === "";

                        return (
                          <li key={key}>
                            <div
                              className={`editable-text-container hover-enabled`}
                              onMouseEnter={() => setIsHovered(key)}
                              onMouseLeave={() => setIsHovered(null)}
                            >
                              <div
                                className={`editable-text ${
                                  isHovered === key ? "blurred" : ""
                                }`}
                              >
                                {isEmpty ? (
                                  <span className="empty-placeholder">
                                    [Click to add text]
                                  </span>
                                ) : (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: currentText,
                                    }}
                                  />
                                )}
                              </div>

                              {isHovered === key && (
                                <button
                                  className="edit-icon"
                                  onClick={() => {
                                    setEditingBaseText({
                                      key,
                                      text: currentText,
                                    });
                                    setShowEditBaseText(true);
                                  }}
                                >
                                  {isEmpty ? (
                                    <i className="bi bi-plus"></i>
                                  ) : (
                                    <i className="bi bi-pencil"></i>
                                  )}
                                </button>
                              )}

                              {showEditBaseText &&
                                editingBaseText?.key === key &&
                                client && (
                                  <EditBaseText
                                    type={"referenceAuthorizationInstruction"}
                                    clientID={client.clientID}
                                    baseTextKey={editingBaseText.key}
                                    baseText={editingBaseText.text}
                                    onClose={(updatedText, successMessage) => {
                                      setInstructions((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              [editingBaseText.key]:
                                                updatedText,
                                            }
                                          : prev,
                                      );
                                      if (successMessage) {
                                        setSuccessMessage(successMessage);
                                      }
                                      setShowEditBaseText(false);
                                    }}
                                  />
                                )}
                              <br />
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </>
                )}

                <AuthorizationForm
                  authorization={authorization}
                  authText={referenceAuthorization?.authorization}
                  validationErrors={validationErrors}
                  onInputChange={handleAuthorizationChange}
                  type={"referenceAuthorization"}
                  isReadOnly={true}
                  isEditing={true}
                  onEditClick={(text) => {
                    setEditingBaseText({
                      key: "authorization" as keyof ReferenceAuthorization,
                      text: text,
                    });
                    setShowEditBaseText(true);
                  }}
                ></AuthorizationForm>

                {showEditBaseText &&
                  editingBaseText?.key === "authorization" &&
                  client && (
                    <EditBaseText
                      type={"referenceAuthorization"}
                      clientID={client.clientID}
                      baseTextKey={editingBaseText.key}
                      baseText={editingBaseText.text}
                      onClose={(updatedText, successMessage) => {
                        setReferenceAuthorization((prev) =>
                          prev
                            ? {
                                ...prev,
                                [editingBaseText.key]: updatedText,
                              }
                            : prev,
                        );
                        if (successMessage) {
                          setSuccessMessage(successMessage);
                        }
                        setShowEditBaseText(false);
                      }}
                    />
                  )}
              </div>
            </>
          )}
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert-message alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EditReferenceAuthorization;
